import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Bot, ChevronLeft, CheckCircle, Shield, Eye, Lock, 
  Users, Mail, Award, Sparkles, FileCheck, Database
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const horseLogo = "https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/h2gv38bu_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%281%29.png";

export default function AIUserPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const certificationBenefits = [
    "Implemented responsible AI use policies",
    "Trained our team on ethical AI practices",
    "Established data protection protocols",
    "Committed to customer transparency",
    "Passed compliance verification"
  ];

  const aiUseCases = [
    "Provide faster customer service responses via chat or voice assistants",
    "Personalize your experience based on your preferences",
    "Generate content recommendations and suggestions",
    "Analyze service usage to improve our offerings",
    "Automate routine communications and follow-ups",
    "Enhance security and fraud detection"
  ];

  const userRights = [
    "Know when you are interacting with AI",
    "Request human assistance at any time",
    "Access data collected about you",
    "Request deletion of your data",
    "Opt out of AI-personalized communications",
    "File a complaint about AI-related practices"
  ];

  const securityMeasures = [
    "Enterprise-grade encryption (in transit and at rest)",
    "Regular security audits and penetration testing",
    "Employee training on data handling",
    "Access controls and authentication requirements",
    "Incident response procedures"
  ];

  return (
    <div className="page-container min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/20 via-transparent to-[#7C3AED]/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Directory
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#10B981] to-[#7C3AED] flex items-center justify-center">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">AI User Policy</h1>
                <p className="text-[#94A3B8]">Effective Date: February 6, 2026</p>
              </div>
            </div>
            
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              LocalBest.ai is committed to transparency about our use of 
              artificial intelligence technologies. This disclosure explains how we use AI in our services, 
              how your data is protected, and your rights regarding AI-powered interactions.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-8">
          
          {/* AI Certification Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-xl p-6 border-2 border-[#10B981]/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#10B981]" />
              </div>
              <h2 className="text-xl font-bold text-white">Our AI Certification</h2>
              <Badge className="bg-[#10B981] text-white ml-auto">
                <Sparkles className="w-3 h-3 mr-1" />
                AI Certified
              </Badge>
            </div>
            <p className="text-[#94A3B8] mb-4">
              LocalBest.ai is proud to be an <strong className="text-white">AI Certified</strong> platform 
              through AI Horse-Powered Technology & Support. This certification means we have:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {certificationBenefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#94A3B8]">
                  <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* How We Use AI */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-[#7C3AED]" />
              </div>
              <h2 className="text-xl font-bold text-white">How We Use AI</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">We may use AI technology to:</p>
            <ul className="space-y-2">
              {aiUseCases.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-[#94A3B8]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Data & Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Database className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <h2 className="text-xl font-bold text-white">Your Data & Privacy</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-2">Data We Collect</h3>
                <ul className="space-y-1 text-[#94A3B8] text-sm">
                  <li>• Information you provide directly (name, email, preferences)</li>
                  <li>• Interaction data (messages, clicks, service usage)</li>
                  <li>• Technical data (device type, browser, IP address)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-2">How AI Uses Your Data</h3>
                <ul className="space-y-1 text-[#94A3B8] text-sm">
                  <li>• To personalize responses and recommendations</li>
                  <li>• To improve our AI systems' accuracy</li>
                  <li>• To analyze trends and service performance</li>
                </ul>
              </div>
              
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-400" />
                  We DO NOT:
                </h3>
                <ul className="space-y-1 text-[#94A3B8] text-sm">
                  <li>• Sell your personal data to third parties</li>
                  <li>• Use your data to train AI models without consent</li>
                  <li>• Store sensitive information in unsecured AI systems</li>
                  <li>• Make automated decisions that significantly affect you without human review</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Your Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#EC4899]" />
              </div>
              <h2 className="text-xl font-bold text-white">Your Rights</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">You have the right to:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {userRights.map((right, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#94A3B8]">
                  <CheckCircle className="w-4 h-4 text-[#EC4899] flex-shrink-0" />
                  <span className="text-sm">{right}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* AI Interaction Disclosure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <h2 className="text-xl font-bold text-white">AI Interaction Disclosure</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">When you interact with our AI systems, you may notice:</p>
            <ul className="space-y-2 text-[#94A3B8] mb-4">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />
                Response indicators showing "AI Assistant" or similar labels
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />
                Slightly faster response times than human agents
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />
                Consistent, helpful answers to common questions
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />
                Seamless handoff to human representatives when needed
              </li>
            </ul>
            <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
              <p className="text-[#94A3B8] text-sm">
                <strong className="text-white">Our AI assistants are designed to be helpful, harmless, and honest.</strong> They will never pretend to be human when directly asked, provide medical, legal, or financial advice without disclaimers, or make promises or commitments we cannot honor.
              </p>
            </div>
          </motion.div>

          {/* Security Measures */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#06B6D4]/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h2 className="text-xl font-bold text-white">Security Measures</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">We protect your data with:</p>
            <ul className="space-y-2">
              {securityMeasures.map((measure, idx) => (
                <li key={idx} className="flex items-start gap-3 text-[#94A3B8]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4] mt-2 flex-shrink-0" />
                  {measure}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Certification Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="glass-card rounded-xl p-6 border-2 border-[#7C3AED]/30 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] p-1 overflow-hidden">
              <img 
                src={horseLogo} 
                alt="AI Horse-Powered" 
                className="w-full h-full rounded-xl object-cover"
              />
            </div>
            <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              Voted Best of Best
            </Badge>
            <h3 className="text-lg font-bold text-white mb-2">AI Certified Business</h3>
            <p className="text-[#94A3B8] text-sm mb-4">
              LocalBest.ai is recognized as an AI Certified platform, 
              demonstrating our commitment to ethical, transparent, and responsible AI use.
            </p>
            <p className="text-xs text-[#64748B]">
              Certified By: AI Horse-Powered Technology & Support
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#7C3AED]" />
              </div>
              <h2 className="text-xl font-bold text-white">Contact Us</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">
              Questions about our AI practices? Contact us:
            </p>
            <div className="space-y-2 text-[#94A3B8] mb-6">
              <p><strong className="text-white">Email:</strong> support@localbest.ai</p>
              <p><strong className="text-white">Website:</strong> https://localbest.ai</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:support@localbest.ai">
                <Button className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </a>
              <Link to="/privacy">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Privacy Policy
                </Button>
              </Link>
              <Link to="/terms">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Terms of Service
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
